/* eslint-disable jsx-a11y/anchor-is-valid */
import axios from 'axios'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useEffect, useState, useCallback} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom'
import * as Yup from 'yup'
import {UserModel} from '../../../../_metronic/global/models'
import {StoreModel} from '../../../redux/types'
import {setCurrentUser} from '../../../redux/currentUser/CurrentUserAction'
// import {useAuth} from '../core/Auth'
import {login, LOGIN_URL, resendVerificationEmail} from '../core/_requests'
import * as genericServices from '../../../../_metronic/global/genericServices'
import {useSnackbar} from 'notistack'
import {removeAuth, setAuth} from '../core/AuthHelpers'
import {API_URL} from '../../../../_metronic/global/data'

const loginSchema = Yup.object().shape({
  email: Yup.string().email('Wrong email format').required('Email is required'),
  password: Yup.string()
    .min(6, 'Password must be 6 characters of length')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const {pathname} = useLocation()
  const isInvitedUser = pathname.includes('inviteuser') ? true : false
  const {id} = useParams()
  const [isValidLink, setIsValidLink] = useState(false)
  const {enqueueSnackbar} = useSnackbar()
  const hasModuleInUrl = pathname.toLowerCase().includes('authenticatemodule')

  const [loading, setLoading] = useState(false)
  const [showReferralScreen, setShowReferralScreen] = useState(false)
  const [referralLoading, setReferralLoading] = useState(false)
  const [referralError, setReferralError] = useState('')
  const [referralCode, setReferralCode] = useState('')
  const [authData, setAuthData] = useState<any>(null)
  const [userData, setUserData] = useState<UserModel | null>(null)
  // const {saveAuth} = useAuth()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(window.location.search)
  const redirectUrl = searchParams.get('redirecturl')
  const moduleUrl = searchParams.get('moduleurl')
  // DiscourseConnect (Discourse SSO) params, appended by Discourse to the
  // DiscourseConnect URL when a community user starts login.
  const discourseSso = searchParams.get('sso')
  const discourseSig = searchParams.get('sig')
  const discourseEndpoint = searchParams.get('endpoint')
  // Salesforce (login-as-auth-provider) state, appended by Salesforce when a
  // user starts login from a Salesforce login page.
  const salesforceState = searchParams.get('sfdcState')
  const [unverifiedEmail, setUnverifiedEmail] = useState<string | null>(null)

  const dispatch = useDispatch()
  const {currentUser} = useSelector((state: StoreModel) => state.globalStates)
  const VerifyLink = useCallback(async () => {
    if (!isInvitedUser) {
      const response = await genericServices.get(`users/is_valid_link/${id}`)
      if (response.status === 200) {
        setIsValidLink(true)
      } else {
        let message = response?.data?.message ?? 'Something went wrong'
        if (id) sessionStorage.setItem(`invite_link_error_${id}`, message)
        enqueueSnackbar(message, {variant: 'error'})
      }
    }
  }, [id, enqueueSnackbar, isInvitedUser])
  const handleResendVerification = async () => {
    if (!unverifiedEmail) return
    // Resending needs the password because it re-authenticates before asking
    // Firebase for a new link. Returning silently here left the user clicking a
    // link that did nothing and said nothing - tell them what's missing.
    if (!formik.values.password) {
      enqueueSnackbar('Please re-enter your password, then click resend.', {variant: 'warning'})
      return
    }

    try {
      await resendVerificationEmail(unverifiedEmail, formik.values.password)
      enqueueSnackbar('Verification email sent successfully!', {variant: 'success'})
    } catch (error) {
      enqueueSnackbar('Failed to send verification email. Please try again.', {variant: 'error'})
    }
  }

  useEffect(() => {
    id && VerifyLink()
  }, [id, VerifyLink])

  // DiscourseConnect SSO: persist the sso/sig/endpoint params so they survive
  // the Firebase login + business-selection navigation and can be read by
  // BusinessModal (mirrors v1 Authenticatemodule.js).
  useEffect(() => {
    if (discourseSso && discourseSig) {
      localStorage.setItem('discourseState_sso', discourseSso)
      localStorage.setItem('discourseState_sig', discourseSig)
      if (discourseEndpoint) {
        localStorage.setItem('discourse_sso_endpoint', discourseEndpoint)
      }
    }
  }, [discourseSso, discourseSig, discourseEndpoint])

  // Salesforce SSO: persist sfdcState so it survives the Firebase login +
  // business-selection navigation and can be read by BusinessModal. URL param
  // is `sfdcState`, localStorage key is `salesforceState` (mirrors v1).
  useEffect(() => {
    if (salesforceState) localStorage.setItem('salesforceState', salesforceState)
  }, [salesforceState])

  // If the user already has an active session, skip the login form and go
  // straight to business selection for the module handoff (mirrors v1's
  // token-present shortcut). The persist effect above runs first, so the
  // Discourse params are already in localStorage before we navigate.
  useEffect(() => {
    if (
      hasModuleInUrl &&
      currentUser &&
      localStorage.getItem('idToken') &&
      moduleUrl &&
      redirectUrl
    ) {
      navigate(
        `/business-list/authenticatemodule/?moduleurl=${encodeURIComponent(
          moduleUrl
        )}&redirecturl=${encodeURIComponent(redirectUrl)}`,
        {
          state: {redirectUrl, moduleUrl},
        }
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasModuleInUrl, currentUser, moduleUrl, redirectUrl])
  //  const callModuleAuthApi = async (businessId: string, moduleUrl: string) => {
  //   try {
  //     const response = await axios.post(`${API_URL}users/module-auth`, {
  //       moduleUrl: moduleUrl,
  //       businessId: businessId
  //     }, {
  //       headers: {
  //         'Authorization': `Bearer ${localStorage.getItem('auth') ? JSON.parse(localStorage.getItem('auth') || '{}').api_token : ''}`,
  //         'Content-Type': 'application/json'
  //       }
  //     })
  //     return response.data

  //   } catch (error) {
  //     console.error('Module authentication API call failed:', error)
  //   }
  // }

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data: auth} = await login(values.email, values.password)
        setAuth(auth, dispatch)
        let payload: any = {require_setting: true}
        if (isValidLink) {
          payload = {...payload, invite_link: id}
        }
        if (isInvitedUser) {
          payload = {...payload, default_business_id: id}
        }
        const {data: user} = await axios.post<UserModel>(LOGIN_URL, payload)
        // If first login, show referral screen and stop navigation WITHOUT setting redux auth/user yet
        if ((user as any).is_first_login) {
          setAuthData(auth)
          setUserData(user)
          setShowReferralScreen(true)
          setLoading(false)
          return
        }
        // Non-first login: complete login and navigate
        dispatch(setCurrentUser({...user, email: values.email}))
        await setAuth({...auth, business_key: user.business_key}, dispatch)
        if (hasModuleInUrl) {
          navigate('/business-list', {})
          if (moduleUrl && redirectUrl) {
            navigate(
              `/business-list/authenticatemodule/?moduleurl=${encodeURIComponent(
                moduleUrl
              )}&redirecturl=${encodeURIComponent(redirectUrl)}`,
              {
                state: {
                  redirectUrl: redirectUrl,
                  moduleUrl: moduleUrl,
                },
              }
            )
            return
          }
        }
        if (user.no_of_business === 0) {
          navigate('/dashboard-new')
        } else {
          if (user.is_user_subscribed === true) {
            if (user.is_owner === true) {
              navigate('/dashboard')
            } else {
              navigate('/app/modules')
            }
          } else if (user.is_pay_by_user) {
            navigate('/app/subscriptions/add')
          } else {
            if (user.is_owner === true) {
              navigate('/dashboard')
            } else {
              navigate('/app/modules')
            }
          }
        }

        // Show invite-link message after navigation (only when user joined via link)
        if (id) {
          const storedInviteError = sessionStorage.getItem(`invite_link_error_${id}`)
          if (storedInviteError) {
            setTimeout(() => {
              enqueueSnackbar(storedInviteError, {
                variant: 'error',
                autoHideDuration: 5000,
              })
              sessionStorage.removeItem(`invite_link_error_${id}`)
            }, 1000)
          } else if (isValidLink) {
            setTimeout(() => {
              enqueueSnackbar(
                'You have been successfully added to this business through an invite link',
                {
                  variant: 'success',
                  autoHideDuration: 5000,
                }
              )
            }, 1000)
          }
        }
      } catch (error: any) {
        if (error.message && error.message.includes('Please verify your email address')) {
          setStatus(error.message)
          setUnverifiedEmail(values.email)
        } else {
          setStatus('Invalid Email or Password')
          setUnverifiedEmail(null)
        }
        removeAuth(dispatch)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  const finalizeAndProceed = async () => {
    const user = userData as any
    if (!user || !authData) return
    // Now set redux auth and user so private routes are active
    dispatch(setCurrentUser({...user, email: formik.values.email}))
    await setAuth({...authData, business_key: user.business_key}, dispatch)
    if (hasModuleInUrl) {
      navigate('/business-list', {})
      if (moduleUrl && redirectUrl) {
        navigate(
          `/business-list/authenticatemodule/?moduleurl=${encodeURIComponent(
            moduleUrl
          )}&redirecturl=${encodeURIComponent(redirectUrl)}`,
          {
            state: {
              redirectUrl: redirectUrl,
              moduleUrl: moduleUrl,
            },
          }
        )
        return
      }
    }
    if (user.no_of_business === 0) {
      navigate('/dashboard-new')
    } else {
      if (user.is_user_subscribed === true) {
        if (user.is_owner === true) {
          navigate('/dashboard')
        } else {
          navigate('/app/modules')
        }
      } else if (user.is_pay_by_user) {
        navigate('/app/subscriptions/add')
      } else {
        if (user.is_owner === true) {
          navigate('/dashboard')
        } else {
          navigate('/app/modules')
        }
      }
    }

    if (id) {
      const storedInviteError = sessionStorage.getItem(`invite_link_error_${id}`)
      if (storedInviteError) {
        setTimeout(() => {
          enqueueSnackbar(storedInviteError, {
            variant: 'error',
            autoHideDuration: 5000,
          })
          sessionStorage.removeItem(`invite_link_error_${id}`)
        }, 1000)
      } else if (isValidLink) {
        setTimeout(() => {
          enqueueSnackbar(
            'You have been successfully added to this business through an invite link',
            {
              variant: 'success',
              autoHideDuration: 5000,
            }
          )
        }, 1000)
      }
    }
  }

  const submitReferral = async () => {
    if (!referralCode || !authData) {
      await finalizeAndProceed()
      return
    }
    try {
      setReferralLoading(true)
      setReferralError('')
      await axios.get(`${API_URL}users/validate-referral/${encodeURIComponent(referralCode)}`, {
        headers: {
          Authorization: `Bearer ${authData.api_token}`,
        },
      })
      enqueueSnackbar('Referral code applied successfully!', {variant: 'success'})
      setShowReferralScreen(false)
      await finalizeAndProceed()
    } catch (error: any) {
      setReferralError(error?.response?.data?.message || 'Invalid referral code')
    } finally {
      setReferralLoading(false)
    }
  }

  return (
    <>
      {showReferralScreen ? (
        <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>Enter Referral Code</h1>
            {/* <div className='text-gray-600'>Get bonus rewards with a referral code</div> */}
          </div>
          {referralError && (
            <div className='mb-5 alert alert-danger'>
              <div className='alert-text font-weight-bold'>{referralError}</div>
            </div>
          )}
          <div className='fv-row mb-10'>
            <label className='form-label fs-6 fw-bolder text-dark'>Referral Code</label>
            <input
              placeholder='Enter referral code'
              className='form-control form-control-lg form-control-solid'
              type='text'
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value)}
              autoComplete='off'
            />
          </div>
          <div className='text-center'>
            <button
              type='button'
              className='btn btn-sm btn-primary w-100 mb-3'
              onClick={submitReferral}
              disabled={referralLoading}
            >
              {referralLoading ? 'Validating...' : 'Apply Code'}
            </button>
            <button
              type='button'
              className='btn btn-sm btn-secondary w-100'
              onClick={finalizeAndProceed}
              disabled={referralLoading}
            >
              Skip
            </button>
          </div>
        </div>
      ) : (
        <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
          <form
            className='form w-100'
            onSubmit={formik.handleSubmit}
            noValidate
            id='kt_login_signin_form'
          >
            {/* begin::Heading */}
            <div className='text-center mb-10'>
              <h1 className='text-dark mb-3'>Sign In to Single Login</h1>
              <div className='text-gray-400 fw-bold fs-4'>
                New Here?{' '}
                <Link
                  to={
                    isInvitedUser
                      ? `/auth/registration/inviteuser/${id}`
                      : id
                      ? `/auth/registration/${id}`
                      : '/auth/registration'
                  }
                  className='link-primary fw-bolder'
                >
                  Create an Account
                </Link>
              </div>
            </div>
            {/* begin::Heading */}
            {formik.status && (
              <div className='mb-lg-15 alert alert-danger'>
                <div className='alert-text font-weight-bold'>
                  {formik.status.replace(/<a[^>]*>.*?<\/a>/g, '')} {/* Remove the HTML link */}
                  {unverifiedEmail && (
                    <>
                      <br />
                      <a
                        href='#'
                        onClick={(e) => {
                          e.preventDefault()
                          handleResendVerification()
                        }}
                        style={{color: '#ffc107', textDecoration: 'underline'}}
                      >
                        Resend verification email
                      </a>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* begin::Form group */}
            <div className='fv-row mb-10'>
              <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
              <input
                placeholder='Email'
                {...formik.getFieldProps('email')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {'is-invalid': formik.touched.email && formik.errors.email},
                  {
                    'is-valid': formik.touched.email && !formik.errors.email,
                  }
                )}
                type='email'
                name='email'
                autoComplete='off'
              />
              {formik.touched.email && formik.errors.email && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.email}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {/* begin::Form group */}
            <div className='fv-row mb-10'>
              <div className='d-flex justify-content-between mt-n5'>
                <div className='d-flex flex-stack mb-2'>
                  {/* begin::Label */}
                  <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
                  {/* end::Label */}
                  {/* begin::Link */}
                  <Link
                    to='/auth/forgot-password'
                    className='link-primary fs-6 fw-bolder'
                    style={{marginLeft: '5px'}}
                  >
                    Forgot Password ?
                  </Link>
                  {/* end::Link */}
                </div>
              </div>
              <input
                type='password'
                autoComplete='off'
                {...formik.getFieldProps('password')}
                className={clsx(
                  'form-control form-control-lg form-control-solid',
                  {
                    'is-invalid': formik.touched.password && formik.errors.password,
                  },
                  {
                    'is-valid': formik.touched.password && !formik.errors.password,
                  }
                )}
              />
              {formik.touched.password && formik.errors.password && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.password}</span>
                  </div>
                </div>
              )}
            </div>
            {/* end::Form group */}

            {/* begin::Action */}
            <div className='text-center'>
              <button
                type='submit'
                id='kt_sign_in_submit'
                className='btn btn-sm btn-primary w-100 mb-5'
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {!loading && <span className='indicator-label'>Continue</span>}
                {loading && (
                  <span className='indicator-progress' style={{display: 'block'}}>
                    Please wait...
                    <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                  </span>
                )}
              </button>
            </div>
            {/* end::Action */}
          </form>
        </div>
      )}
    </>
  )
}
